import {
  Module, VuexModule, Mutation, Action,
} from 'vuex-module-decorators';
import * as amplitude from 'amplitude-js';

import {
  SupplierItemDTO, SupplierItemDTOSupplierStatusEnum,
} from '@/api';
import service from '@/services';
import { throwError } from '@/utils';

@Module({ name: 'SupplierModule', namespaced: true })
export default class SupplierModule extends VuexModule {
  loading = false;

  things: SupplierItemDTO[] = [];

  selected: number | null = null;

  get selectedThing() {
    return this.things.find(thing => thing.id && (this.selected === thing.id)) || null;
  }

  @Mutation
  setLoading(value: boolean) {
    this.loading = value;
  }

  @Mutation
  setSelected(id: number | null) {
    this.selected = id;
  }

  @Mutation
  setThings(list: SupplierItemDTO[]) {
    this.things = list;
  }

  @Mutation
  updateThing(model: SupplierItemDTO) {
    const index = this.things.findIndex(item => item.id === model.id);
    if (index > -1) {
      Object.assign(this.things[index], model);
    } else {
      this.things.unshift(model);
    }
  }

  @Action({ rawError: true })
  async fetchThings() {
    try {
      this.setLoading(true);
      const { data } = await service.supplierApi().supplierEndpointListItems();
      this.setThings(data);
      amplitude.getInstance().logEventWithGroups('Загрузка вещей поставщика', data.length, 'Поставщик');
      return data;
    } catch (e) {
      return throwError('Ошибка загрузки списка вещей')(e);
    } finally {
      this.setLoading(false);
    }
  }

  @Action({ rawError: true })
  async setReserved(supplierItem: SupplierItemDTO) {
    try {
      this.setLoading(true);
      if (supplierItem.id != null) {
        await service.supplierApi().supplierEndpointSetReserved(supplierItem.id);
      }
      const item = {
        ...supplierItem,
        supplierStatus: SupplierItemDTOSupplierStatusEnum.RESERVED,
      };
      this.updateThing(item);
      amplitude.getInstance().logEventWithGroups('Зарезервировано', supplierItem, 'Поставщик');
      return item;
    } catch (e) {
      return throwError('Ошибка сохранения вещей')(e);
    } finally {
      this.setLoading(false);
    }
  }

  @Action({ rawError: true })
  async setNewPrice({ supplierItem, price }: { supplierItem: SupplierItemDTO; price: number }) {
    try {
      this.setLoading(true);
      if (supplierItem.id != null) {
        await service.supplierApi().supplierEndpointNewPrice(supplierItem.id, price);
      }
      const item = {
        ...supplierItem,
        supplierStatus: SupplierItemDTOSupplierStatusEnum.PRICE_CHANGED,
      };
      this.updateThing(item);
      amplitude.getInstance().logEventWithGroups('Новая цена', { supplierItem, price }, 'Поставщик');
      return item;
    } catch (e) {
      return throwError('Ошибка сохранения вещей')(e);
    } finally {
      this.setLoading(false);
    }
  }

  @Action({ rawError: true })
  async setOutOfStock({ supplierItem, replacementLink }:
    {supplierItem: SupplierItemDTO; replacementLink: string}) {
    try {
      this.setLoading(true);
      this.setLoading(true);
      if (supplierItem.id != null) {
        await service.supplierApi().supplierEndpointOutOfStock(supplierItem.id, replacementLink);
      }
      const item = {
        ...supplierItem,
        supplierStatus: SupplierItemDTOSupplierStatusEnum.MISSING,
      };
      this.updateThing(item);
      amplitude.getInstance().logEventWithGroups('Нет в наличии', { supplierItem, replacementLink }, 'Поставщик');
      return item;
    } catch (e) {
      return throwError('Ошибка сохранения вещей')(e);
    } finally {
      this.setLoading(false);
    }
  }

  @Action({ rawError: true })
  async setReadyForDelivery(supplierItem: SupplierItemDTO) {
    try {
      this.setLoading(true);
      if (supplierItem.id != null) {
        await service.supplierApi().supplierEndpointSetReadyForDelivery(supplierItem.id);
      }
      const item = {
        ...supplierItem,
        supplierStatus: SupplierItemDTOSupplierStatusEnum.READY_FOR_DELIVERY,
      };
      this.updateThing(item);
      amplitude.getInstance().logEventWithGroups('Готов к доставке', supplierItem, 'Поставщик');
      return item;
    } catch (e) {
      return throwError('Ошибка сохранения вещей')(e);
    } finally {
      this.setLoading(false);
    }
  }
}
